import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Viewer, Worker, SpecialZoomLevel } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import { zoomPlugin } from "@react-pdf-viewer/zoom";
import { pageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import { thumbnailPlugin } from "@react-pdf-viewer/thumbnail";
import "@react-pdf-viewer/thumbnail/lib/styles/index.css";
import { searchPlugin } from "@react-pdf-viewer/search";
import "@react-pdf-viewer/search/lib/styles/index.css";
import pdfjsPackage from "pdfjs-dist/package.json";
import {
  Sparkles,
  Loader2,
  BookOpen,
  PenLine,
  Copy,
  X,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Send,
  Inbox,
  PanelLeftClose,
  PanelLeft,
  Highlighter,
} from "lucide-react";
import styles from "../styles/pdfHighlightReader.module.css";

const API_BASE =
  process.env.REACT_APP_API_URL || "http://localhost:5001";

const WORKER_URL = `https://unpkg.com/pdfjs-dist@${pdfjsPackage.version}/build/pdf.worker.min.js`;

const MIN_SELECTION_CHARS = 8;
const MAX_SELECTION_CHARS = 4000;

/**
 * PDF reader with fit-width zoom, page nav, selection toolbar, and an inline
 * AI panel (no jumping to Study). Optional Lumi: open chat + send vs queue draft only.
 *
 * `onAddToNotes(text, meta?)` — meta.kind === 'ai_summary' inserts structured heading + body;
 * otherwise a quote block. Implementations should accept inserts even when no page is focused.
 */
const PdfHighlightReader = ({
  doc,
  onExplainStart,
  onAddToNotes,
  /** Switch to Lumi & auto-send this message */
  onOpenLumiChat,
  /** Append to Lumi input without switching tabs */
  onQueueLumiDraft,
  onAnnotate,
  annotations = [],
}) => {
  const containerRef = useRef(null);
  const viewerShellRef = useRef(null);
  const highlightApiRef = useRef({ highlight: null, clearHighlights: null });

  // Call plugin factories on every render — they use hooks internally; wrapping them in
  // useMemo(() => …, []) violates the Rules of Hooks and triggers "Should have a queue".
  const zoomPluginInstance = zoomPlugin({ enableShortcuts: true });
  const pageNavPluginInstance = pageNavigationPlugin();
  const thumbnailPluginInstance = thumbnailPlugin({ thumbnailWidth: 108 });
  const searchPluginInstance = searchPlugin();

  const { zoomTo, ZoomIn: ZoomInCtl, ZoomOut: ZoomOutCtl } = zoomPluginInstance;
  const { jumpToNextPage, jumpToPreviousPage } = pageNavPluginInstance;
  const { Thumbnails } = thumbnailPluginInstance;
  const { highlight, clearHighlights } = searchPluginInstance;
  highlightApiRef.current.highlight = highlight;
  highlightApiRef.current.clearHighlights = clearHighlights;

  const [showThumbnails, setShowThumbnails] = useState(true);

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selection, setSelection] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copiedAi, setCopiedAi] = useState(false);
  const [savedHighlights, setSavedHighlights] = useState([]);

  /** Inline AI rail: idle | loading | ready | error */
  const [inlineAi, setInlineAi] = useState(null);

  const fileUrl = useMemo(() => {
    if (!doc?.content) return null;
    const type = doc.type || "application/pdf";
    return `data:${type};base64,${doc.content}`;
  }, [doc?.content, doc?.type]);

  const fitWidth = useCallback(() => {
    try {
      zoomTo(SpecialZoomLevel.PageWidth);
    } catch {
      /* ignore */
    }
  }, [zoomTo]);

  const getSurroundingContext = useCallback(
    (pageIdx) => {
      if (doc?.pages && Array.isArray(doc.pages) && doc.pages[pageIdx]) {
        return doc.pages[pageIdx]?.text || "";
      }
      if (doc?.extractedText && typeof doc.extractedText === "string") {
        return doc.extractedText.slice(0, 8000);
      }
      return "";
    },
    [doc?.pages, doc?.extractedText],
  );

  // Fit on initial load + structural layout changes. Do not auto-fit continuously,
  // otherwise pinch-to-zoom/wheel zoom gets reset and feels broken.
  useEffect(() => {
    if (!fileUrl) return;
    const raf = requestAnimationFrame(() => fitWidth());
    return () => cancelAnimationFrame(raf);
  }, [fileUrl, showThumbnails, inlineAi?.status, fitWidth]);

  useEffect(() => {
    const snippetsFromAnnotations =
      (annotations || [])
        .map((a) => (a?.quote || "").replace(/\s+/g, " ").trim())
        .filter(Boolean) || [];
    if (!snippetsFromAnnotations.length) return;
    setSavedHighlights((prev) => {
      const merged = [...prev];
      snippetsFromAnnotations.forEach((snippet) => {
        if (!merged.includes(snippet)) merged.push(snippet);
      });
      return merged.slice(0, 120);
    });
  }, [annotations]);

  useEffect(() => {
    const clearFn = highlightApiRef.current.clearHighlights;
    const highlightFn = highlightApiRef.current.highlight;
    if (!clearFn || !highlightFn) return;
    clearFn();
    if (!savedHighlights.length) return;
    highlightFn(savedHighlights).catch(() => {});
  }, [savedHighlights]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const handleMouseUp = (e) => {
      setTimeout(() => {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) {
          setSelection(null);
          return;
        }
        const text = sel.toString().trim();
        if (!text || text.length < MIN_SELECTION_CHARS) {
          setSelection(null);
          return;
        }
        const anchorNode = sel.anchorNode;
        if (anchorNode && !el.contains(anchorNode)) {
          setSelection(null);
          return;
        }

        const rect = el.getBoundingClientRect();
        const bubbleW = 420;
        const x = Math.min(
          Math.max(e.clientX - rect.left - bubbleW / 2, 8),
          Math.max(8, rect.width - bubbleW - 8),
        );
        const y = Math.min(
          Math.max(e.clientY - rect.top + 10, 8),
          Math.max(8, rect.height - 52),
        );
        setSelection({ text: text.slice(0, MAX_SELECTION_CHARS), x, y });
      }, 12);
    };

    el.addEventListener("mouseup", handleMouseUp);
    return () => el.removeEventListener("mouseup", handleMouseUp);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (inlineAi) setInlineAi(null);
        else setSelection(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [inlineAi]);

  const dismissSelection = useCallback(() => {
    setSelection(null);
    try {
      window.getSelection()?.removeAllRanges();
    } catch {
      /* ignore */
    }
  }, []);

  const handleExplain = async () => {
    if (!selection?.text) return;
    setError(null);
    setLoading(true);
    setInlineAi({ status: "loading", mode: "explain" });

    const pageNumber = currentPage + 1;
    const meta = {
      doc_name: doc?.name || "Document",
      page: pageNumber,
      selected_text: selection.text,
      mode: "explain",
    };
    if (onExplainStart) {
      try {
        onExplainStart(meta);
      } catch {
        /* ignore */
      }
    }

    try {
      const res = await fetch(`${API_BASE}/api/explain_highlight`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          selected_text: selection.text,
          doc_name: doc?.name || "Document",
          page: pageNumber,
          surrounding_context: getSurroundingContext(currentPage),
          mode: "explain",
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      data.mode = "explain";
      data.source = {
        doc_name: doc?.name,
        page: pageNumber,
        selected_text: selection.text,
      };
      setInlineAi({ status: "ready", mode: "explain", data });
      dismissSelection();
    } catch (err) {
      console.error("explain_highlight failed:", err);
      setInlineAi({ status: "error", mode: "explain", error: err.message || "Failed" });
      setError(err.message || "Failed to explain");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = useCallback((e) => {
    setCurrentPage(typeof e?.currentPage === "number" ? e.currentPage : 0);
  }, []);

  const handleDocumentLoad = useCallback(
    (e) => {
      setTotalPages(e?.doc?.numPages || 0);
      requestAnimationFrame(() => fitWidth());
    },
    [fitWidth],
  );

  const pdfCaptureMeta = useMemo(
    () => ({
      kind: "selection_quote",
      docName: doc?.name,
      page: currentPage + 1,
    }),
    [doc?.name, currentPage],
  );

  const handleAddToNotes = useCallback(() => {
    if (!selection?.text || !onAddToNotes) return;
    onAddToNotes(selection.text, pdfCaptureMeta);
    dismissSelection();
  }, [selection, onAddToNotes, dismissSelection, pdfCaptureMeta]);

  const buildLumiMessage = useCallback(
    (prefix) =>
      `${prefix}\n\nFrom "${doc?.name || "document"}", page ${currentPage + 1}:\n\n"${selection?.text || ""}"`,
    [doc, currentPage, selection],
  );

  const handleOpenLumi = useCallback(() => {
    if (!selection?.text || !onOpenLumiChat) return;
    onOpenLumiChat(
      buildLumiMessage(
        "Please explain this passage clearly and connect it to anything I should memorize for exams.",
      ),
    );
    dismissSelection();
  }, [selection, onOpenLumiChat, buildLumiMessage, dismissSelection]);

  const handleQueueLumi = useCallback(() => {
    if (!selection?.text || !onQueueLumiDraft) return;
    onQueueLumiDraft(
      buildLumiMessage(
        "[Queued from PDF — please explain this passage and suggest review steps.]",
      ),
    );
    dismissSelection();
  }, [selection, onQueueLumiDraft, buildLumiMessage, dismissSelection]);

  const handleAnnotateAction = useCallback(() => {
    if (!selection?.text) return;
    const normalized = selection.text.replace(/\s+/g, " ").trim();
    if (onAnnotate) onAnnotate(selection.text);
    if (normalized) {
      setSavedHighlights((prev) => {
        if (prev.includes(normalized)) return prev;
        return [...prev, normalized].slice(-120);
      });
    }
    dismissSelection();
  }, [selection, onAnnotate, dismissSelection]);

  const handleCopy = useCallback(() => {
    if (!selection?.text) return;
    navigator.clipboard.writeText(selection.text).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [selection]);

  const insertAiSummaryIntoNotes = useCallback(() => {
    if (!inlineAi?.data || !onAddToNotes) return;
    const d = inlineAi.data;
    const chunks = [];
    if (d.explanation) chunks.push(d.explanation);
    if (Array.isArray(d.key_points) && d.key_points.length) {
      chunks.push("\nKey points:\n" + d.key_points.map((p) => `• ${p}`).join("\n"));
    }
    if (Array.isArray(d.jargon) && d.jargon.length) {
      chunks.push(
        "\nTerms:\n" + d.jargon.map((j) => `• ${j.term}: ${j.definition}`).join("\n"),
      );
    }
    onAddToNotes(chunks.join("\n"), {
      kind: "ai_summary",
      docName: d.source?.doc_name || doc?.name,
      page: d.source?.page ?? currentPage + 1,
    });
  }, [inlineAi, onAddToNotes, doc?.name, currentPage]);

  const copyAiPayloadToClipboard = useCallback(() => {
    if (!inlineAi?.data) return;
    const d = inlineAi.data;
    const lines = [];
    if (d.source?.selected_text) lines.push(`Selection:\n"${d.source.selected_text}"\n`);
    if (d.explanation) lines.push(d.explanation);
    if (d.key_points?.length) lines.push("\nKey points:\n" + d.key_points.map((p) => `• ${p}`).join("\n"));
    if (d.jargon?.length) lines.push("\nTerms:\n" + d.jargon.map((j) => `• ${j.term}: ${j.definition}`).join("\n"));
    navigator.clipboard.writeText(lines.join("\n")).catch(() => {});
    setCopiedAi(true);
    setTimeout(() => setCopiedAi(false), 1600);
  }, [inlineAi]);

  const sendAiToLumiOpen = useCallback(() => {
    if (!inlineAi?.data || !onOpenLumiChat) return;
    const d = inlineAi.data;
    let msg = `Here's what I understood from my PDF:\n\n${d.explanation || ""}`;
    if (d.key_points?.length) msg += `\n\nKey points:\n${d.key_points.map((p) => `• ${p}`).join("\n")}`;
    onOpenLumiChat(msg);
  }, [inlineAi, onOpenLumiChat]);

  const sendAiToLumiDraft = useCallback(() => {
    if (!inlineAi?.data || !onQueueLumiDraft) return;
    const d = inlineAi.data;
    let msg = `[PDF AI summary]\n\n${d.explanation || ""}`;
    if (d.key_points?.length) msg += `\n\n${d.key_points.map((p) => `• ${p}`).join("\n")}`;
    onQueueLumiDraft(msg);
  }, [inlineAi, onQueueLumiDraft]);

  if (!fileUrl) {
    return (
      <div className={styles.emptyState}>
        <BookOpen size={36} />
        <p>No document content available.</p>
        <p className={styles.emptyHint}>
          Upload a PDF and wait for indexing to finish, then try again.
        </p>
      </div>
    );
  }

  const pageDisplay =
    totalPages > 0 ? `Page ${currentPage + 1} / ${totalPages}` : `Page ${currentPage + 1}`;

  const plugins = [
    thumbnailPluginInstance,
    searchPluginInstance,
    zoomPluginInstance,
    pageNavPluginInstance,
  ];

  const aiOpen =
    inlineAi && ["loading", "ready", "error"].includes(inlineAi.status);

  return (
    <div className={styles.readerRoot}>
      <div className={`${styles.readerBody} ${aiOpen ? styles.readerBodyWithAi : ""}`}>
        <div className={styles.readerMain}>
          <div className={styles.pdfToolbar}>
            <div className={styles.toolCluster}>
              <button
                type="button"
                className={styles.toolBtn}
                onClick={() => jumpToPreviousPage()}
                title="Previous page"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                type="button"
                className={styles.toolBtn}
                onClick={() => jumpToNextPage()}
                title="Next page"
              >
                <ChevronRight size={16} />
              </button>
              <span className={styles.pagePill}>{pageDisplay}</span>
            </div>
            <div className={styles.toolCluster}>
              <ZoomOutCtl>
                {(zprops) => (
                  <button type="button" className={styles.toolBtn} onClick={zprops.onClick} title="Zoom out">
                    <ZoomOut size={15} />
                  </button>
                )}
              </ZoomOutCtl>
              <ZoomInCtl>
                {(zprops) => (
                  <button type="button" className={styles.toolBtn} onClick={zprops.onClick} title="Zoom in">
                    <ZoomIn size={15} />
                  </button>
                )}
              </ZoomInCtl>
              <button type="button" className={styles.toolBtnPrimary} onClick={fitWidth}>
                <Maximize2 size={14} />
                <span>Fit</span>
              </button>
              <button
                type="button"
                className={`${styles.toolBtn} ${showThumbnails ? styles.toolBtnActive : ""}`}
                onClick={() => setShowThumbnails((v) => !v)}
              >
                {showThumbnails ? <PanelLeftClose size={15} /> : <PanelLeft size={15} />}
              </button>
            </div>
          </div>

          <div ref={viewerShellRef} className={styles.viewerShell}>
            <Worker workerUrl={WORKER_URL}>
              <div className={styles.viewerRow}>
                {showThumbnails && (
                  <aside className={styles.thumbnailRail} aria-label="Page thumbnails">
                    <Thumbnails />
                  </aside>
                )}
                <div ref={containerRef} className={styles.viewerWrap}>
                  <Viewer
                    fileUrl={fileUrl}
                    plugins={plugins}
                    onPageChange={handlePageChange}
                    onDocumentLoad={handleDocumentLoad}
                  />
                  {selection && (
                    <div
                      className={styles.explainBubble}
                      style={{ left: selection.x, top: selection.y }}
                      role="toolbar"
                      aria-label="Selection actions"
                    >
                      <button type="button" className={styles.explainBtn} disabled={loading} onClick={handleExplain}>
                        {loading ? <Loader2 size={13} className={styles.spin} /> : <Sparkles size={13} />}
                        Ask AI
                      </button>
                      <button type="button" className={styles.explainBtnAlt} onClick={handleAnnotateAction} title="Keep this highlighted">
                        <Highlighter size={13} />
                        Highlight
                      </button>
                      {onAddToNotes && (
                        <button type="button" className={styles.explainBtnAlt} onClick={handleAddToNotes}>
                          <PenLine size={13} />
                          To note
                        </button>
                      )}
                      {onOpenLumiChat && (
                        <button type="button" className={styles.explainBtnWarm} onClick={handleOpenLumi}>
                          <Send size={13} />
                          Lumi
                        </button>
                      )}
                      {onQueueLumiDraft && (
                        <button type="button" className={styles.explainBtnAlt} onClick={handleQueueLumi}>
                          <Inbox size={13} />
                          Draft
                        </button>
                      )}
                      <button type="button" className={styles.explainBtnAlt} onClick={handleCopy}>
                        <Copy size={13} />
                        {copied ? "✓" : "Copy"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </Worker>
          </div>

          {error && (
            <div className={styles.errorBar}>
              {error}
              <button type="button" onClick={() => setError(null)} className={styles.errorDismiss} aria-label="Dismiss">
                ×
              </button>
            </div>
          )}

        </div>

        {aiOpen && (
          <aside className={styles.aiRail} aria-live="polite">
            <div className={styles.aiRailHeader}>
              <span className={styles.aiRailTitle}>
                <Sparkles size={14} />
                {inlineAi.status === "loading" ? "Thinking…" : inlineAi.status === "error" ? "Something went wrong" : "Passage help"}
              </span>
              <button type="button" className={styles.aiRailClose} onClick={() => setInlineAi(null)} aria-label="Close AI panel">
                <X size={16} />
              </button>
            </div>

            {inlineAi.status === "loading" && (
              <div className={styles.aiRailSkeleton}>
                <div className={styles.skelLine} />
                <div className={styles.skelLine} />
                <div className={styles.skelLineShort} />
              </div>
            )}

            {inlineAi.status === "error" && (
              <p className={styles.aiRailError}>{inlineAi.error}</p>
            )}

            {inlineAi.status === "ready" && inlineAi.data && (
              <div className={styles.aiRailBody}>
                {inlineAi.data.source?.selected_text && (
                  <blockquote className={styles.aiQuote}>
                    {inlineAi.data.source.selected_text.slice(0, 280)}
                    {inlineAi.data.source.selected_text.length > 280 ? "…" : ""}
                  </blockquote>
                )}
                <div className={styles.aiExplanation}>
                  {inlineAi.data.explanation
                    ? inlineAi.data.explanation.split(/\n\n+/).map((para, i) => (
                        <p key={i} className={styles.aiParagraph}>
                          {para.split("\n").map((line, j, arr) => (
                            <span key={j}>
                              {line}
                              {j < arr.length - 1 ? <br /> : null}
                            </span>
                          ))}
                        </p>
                      ))
                    : null}
                </div>
                {inlineAi.data.key_points?.length > 0 && (
                  <div className={styles.aiSection}>
                    <div className={styles.aiSectionLabel}>Takeaways</div>
                    <ul className={styles.aiList}>
                      {inlineAi.data.key_points.map((p, i) => (
                        <li key={i}>{p}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {inlineAi.data.jargon?.length > 0 && (
                  <div className={styles.aiSection}>
                    <div className={styles.aiSectionLabel}>Terms</div>
                    <dl className={styles.aiJargon}>
                      {inlineAi.data.jargon.map((j, i) => (
                        <div key={i}>
                          <dt>{j.term}</dt>
                          <dd>{j.definition}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}

                <div className={styles.aiRailActions}>
                  <button type="button" className={styles.aiActionGhost} onClick={copyAiPayloadToClipboard}>
                    <Copy size={14} />
                    {copiedAi ? "Copied" : "Copy answer"}
                  </button>
                  {onAddToNotes && (
                    <button type="button" className={styles.aiActionPrimary} onClick={insertAiSummaryIntoNotes}>
                      Insert into note
                    </button>
                  )}
                  {onQueueLumiDraft && (
                    <button type="button" className={styles.aiActionSecondary} onClick={sendAiToLumiDraft}>
                      Add to Lumi draft
                    </button>
                  )}
                  {onOpenLumiChat && (
                    <button type="button" className={styles.aiActionSecondary} onClick={sendAiToLumiOpen}>
                      Open Lumi &amp; send
                    </button>
                  )}
                </div>
              </div>
            )}
          </aside>
        )}
      </div>
    </div>
  );
};

export default PdfHighlightReader;
